import React, { useEffect, useState, useRef } from 'react';
import { RangeSlider, Text, Group, Loader } from '@mantine/core';
import * as d3 from 'd3';

// Моковый фид winrate-гистограммы: {winrate: count}
const mockWinrateHistogram: Record<number, number> = {};
for (let i = 1; i <= 100; i++) {
  // Пример: синтетическая кривая с пиком в районе 50-60%
  mockWinrateHistogram[i] =
    Math.round(
      5000 +
        40000 *
          Math.exp(-Math.pow((i - 55) / 18, 2)) + // Гауссов пик
        2000 * Math.sin(i / 8) // немного шума
    );
}

interface WinrateHistogramSliderProps {
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  histogram?: number[]; // Array of bin counts, length 100 or 101
  fillColor?: string; // NEW: color for slider fill
}

const getHistogramArray = (hist: Record<number, number>) => {
  const arr: number[] = [];
  for (let i = 1; i <= 100; i++) {
    arr.push(hist[i] || 0);
  }
  return arr;
};

const sliderTrackBg = (histogram?: number[]) => {
  if (!histogram || histogram.length === 0) return undefined;
  // Normalize and build a linear-gradient for the track
  const maxVal = Math.max(...histogram);
  if (maxVal === 0) return undefined;
  const stops = histogram.map((v, i) => {
    const pct = Math.round((v / maxVal) * 100);
    const color = `rgba(79,70,229,${pct / 200 + 0.2})`; // blue, alpha 0.2-0.7
    return `${color} ${(i / histogram.length) * 100}%`;
  });
  return `linear-gradient(90deg, ${stops.join(', ')})`;
};

export const WinrateHistogramSlider: React.FC<WinrateHistogramSliderProps> = ({
  min,
  max,
  value,
  onChange,
  histogram,
  fillColor = '#10A981' // default green
}) => {
  const [loading, setLoading] = useState(true);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 400);
  }, []);

  // D3 histogram rendering (skip 0% and 100% for visualization)
  useEffect(() => {
    if (!svgRef.current || loading || !histogram || histogram.length === 0) return;
    const width = 320;
    const height = 48;
    // Exclude 0% and 100% for visualization
    const data = histogram.slice(1, 100);
    const maxCount = Math.max(...data, 1);

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // X scale: 1-99% на всю ширину
    const x = d3.scaleLinear().domain([0, data.length]).range([0, width]);
    // Y scale: count -> высота
    const y = d3.scaleLinear().domain([0, maxCount]).range([height, 0]);

    // Area shape для smooth визуализации
    const area = d3
      .area<number>()
      .x((_, i) => x(i))
      .y0(height)
      .y1(d => y(d))
      .curve(d3.curveMonotoneX);

    // Draw area
    svg
      .append('path')
      .datum(data)
      .attr('d', area as any)
      .attr('fill', 'url(#winrate-gradient)')
      .attr('opacity', 0.85);

    // Draw bars поверх area для наглядности
    svg
      .selectAll('rect')
      .data(data)
      .enter()
      .append('rect')
      .attr('x', (_, i) => x(i))
      .attr('y', d => y(d))
      .attr('width', width / data.length - 0.2)
      .attr('height', d => height - y(d))
      .attr('fill', (_, i) => {
        // Индексы слайдера: value[0]..value[1], индексы массива 0..99, winrate = i+1
        const winrate = i + 1;
        if (winrate >= value[0] && winrate <= value[1]) {
          return '#10B981';
        }
        return '#6366f1';
      })
      .attr('opacity', (_, i) => {
        const winrate = i + 1;
        if (winrate >= value[0] && winrate <= value[1]) {
          return 0.7;
        }
        return 0.25;
      })
      .attr('rx', 1);

    // Градиент для area
    svg
      .append('defs')
      .append('linearGradient')
      .attr('id', 'winrate-gradient')
      .attr('x1', '0%')
      .attr('x2', '0%')
      .attr('y1', '0%')
      .attr('y2', '100%')
      .selectAll('stop')
      .data([
        { offset: '0%', color: '#6366f1', opacity: 0.8 },
        { offset: '100%', color: '#6366f1', opacity: 0.1 },
      ])
      .enter()
      .append('stop')
      .attr('offset', d => d.offset)
      .attr('stop-color', d => d.color)
      .attr('stop-opacity', d => d.opacity);
  }, [histogram, loading, value]);

  // Calculate percent of wallets in selected range (use all bins)
  const total = histogram && histogram.length > 0 ? histogram.reduce((a, b) => a + b, 0) : 0;
  const selected = histogram && histogram.length > 0
    ? histogram
        .slice(Math.max(0, value[0]), Math.min(101, value[1] + 1))
        .reduce((a, b) => a + b, 0)
    : 0;
  const percent = total > 0 ? ((selected / total) * 100).toFixed(1) : '0.0';

  return (
    <div style={{ width: '100%', padding: '8px 0' }}>
      <Group justify="space-between" mb={4}>
        <Text size="sm" fw={500}>Winrate (%)</Text>
        <Group gap={8}>
          <Text size="xs" c="dimmed">{value[0]} – {value[1]}</Text>
          <Text size="xs" c="green" fw={600} style={{ minWidth: 52, textAlign: 'right' }}>
            {percent}% wallets
          </Text>
        </Group>
      </Group>
      <div style={{ position: 'relative', height: 32, marginBottom: 0 }}>
        <div style={{
          position: 'absolute',
          left: 0, right: 0, top: 0, bottom: 0,
          zIndex: 2,
          pointerEvents: 'auto',
          padding: '0 2px'
        }}>
          <RangeSlider
            min={min}
            max={max}
            value={value}
            onChange={onChange}
            minRange={3}
            step={1}
            size="xs"
            styles={{
              track: { background: 'transparent', height: 16 },
              bar: { background: fillColor, height: 16 }, // GREEN fill
              thumb: { border: `2px solid ${fillColor}`, background: '#fff', width: 18, height: 18 },
            }}
            thumbSize={18}
            label={v => `${v}%`}
          />
        </div>
      </div>
      {/* D3 отражённая гистограмма */}
      <div style={{
        width: '100%',
        height: 48,
        marginTop: 2,
        display: 'flex',
        alignItems: 'flex-start',
        pointerEvents: 'none',
        transform: 'scaleY(-1)',
        zIndex: 1,
        justifyContent: 'center'
      }}>
        {loading || !histogram || histogram.length === 0 ? (
          <div style={{ width: 320, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 48 }}>
            <Loader size="sm" />
          </div>
        ) : (
          <svg
            ref={svgRef}
            width={320}
            height={48}
            style={{ display: 'block', width: '100%', maxWidth: 320 }}
          />
        )}
      </div>
    </div>
  );
};

export default WinrateHistogramSlider;
